/**
 * AIInsightModal — shows AI-generated insights for a financial metric.
 *
 * Opened when the user clicks on a trend projection badge in EnhancedMetricCard.
 * Contains summary, attention points, suggestions, and a button to open
 * the detailed calculation modal (TrendExplanationModal).
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Sparkles, AlertTriangle, Lightbulb, Calculator, Bot } from 'lucide-react'
import type { AIInsight } from '@/lib/services/ai-insights.service'

interface AIInsightModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  insight: AIInsight | null
  onOpenCalculation?: () => void
}

export function AIInsightModal({ open, onOpenChange, insight, onOpenCalculation }: AIInsightModalProps) {
  if (!insight) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl bg-brand-dark-light border-brand-dark-lighter text-white"
        overlayClassName="bg-black/80"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display text-xl flex items-center gap-2">
            <Sparkles size={20} className="text-brand-primary" />
            Insights — {insight.metricLabel}
          </DialogTitle>
          <DialogDescription className="text-brand-gray-light flex items-center gap-1.5">
            <Bot size={14} />
            Análise gerada pelo assistente de IA
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Summary */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1 rounded bg-brand-primary/10">
                <Sparkles size={14} className="text-brand-primary" />
              </div>
              <h3 className="text-xs font-semibold text-brand-gray uppercase tracking-wider">
                Resumo
              </h3>
            </div>
            <p className="text-sm text-brand-gray-light leading-relaxed">
              {insight.summary}
            </p>
          </div>

          {/* Attention Points */}
          {insight.attentionPoints.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1 rounded bg-yellow-500/10">
                  <AlertTriangle size={14} className="text-yellow-400" />
                </div>
                <h3 className="text-xs font-semibold text-brand-gray uppercase tracking-wider">
                  Pontos de Atenção
                </h3>
              </div>
              <ul className="space-y-2">
                {insight.attentionPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-brand-gray-light">
                    <span className="text-yellow-400 mt-1 flex-shrink-0">•</span>
                    <span className="leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestions */}
          {insight.suggestions.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1 rounded bg-brand-primary/10">
                  <Lightbulb size={14} className="text-brand-primary" />
                </div>
                <h3 className="text-xs font-semibold text-brand-gray uppercase tracking-wider">
                  Sugestões
                </h3>
              </div>
              <ul className="space-y-2">
                {insight.suggestions.map((suggestion, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-brand-gray-light">
                    <span className="text-brand-primary mt-1 flex-shrink-0">•</span>
                    <span className="leading-relaxed">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-brand-dark-lighter">
          <p className="text-[10px] text-brand-gray">
            Gerado em {new Date(insight.generatedAt).toLocaleString('pt-BR')}
          </p>
          <div className="flex gap-2">
            {onOpenCalculation && (
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false)
                  onOpenCalculation()
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-primary text-brand-dark hover:bg-brand-primary/90 transition-colors"
              >
                <Calculator size={14} />
                Ver cálculo detalhado
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-brand-gray hover:text-white transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
